import { Coordinates } from "@/custom-types/Coordinates";
import {
  MapData,
  MapMarkerDisplayTypeEnum,
  Store,
} from "@/custom-types/StoreLocator";
import initializeGoogleMapsLatLng from "@/utils/maps/initializeGoogleMapsLatLng";
import initializeGoogleMapsLatLngBounds from "@/utils/maps/initializeGoogleMapsLatLngBounds";

import { getMilesBetween } from "../distance";

export type Bounds = {
  nw: Coordinates;
  ne: Coordinates;
  sw: Coordinates;
  se: Coordinates;
};

export type BoundsNWSECorners = Pick<Bounds, "nw" | "se">;

export type GetFittedBoundsParams = {
  mapData: MapData;
  storesShown?: number;
  stores: Store[];
  forceReturn?: boolean;
};

export type FittedBounds = {
  center: {
    lat: number;
    lon: number;
  };
  zoom: number;
  newBounds: Bounds;
};

export const getFittedBounds = async ({
  mapData,
  storesShown = 5,
  stores,
  forceReturn = false,
}: GetFittedBoundsParams) => {
  let bounds:
    | (google.maps.LatLngBounds & { isSinglePoint?: boolean })
    | undefined;

  if (mapData?.bounds) {
    const northWest = await initializeGoogleMapsLatLng(
      mapData.bounds.north,
      mapData.bounds.west,
    );
    const southEast = await initializeGoogleMapsLatLng(
      mapData.bounds.south,
      mapData.bounds.east,
    );
    bounds = await initializeGoogleMapsLatLngBounds(northWest, southEast);
  } else if (mapData?.center) {
    bounds = await initializeGoogleMapsLatLngBounds();
    const centerLatLng = await initializeGoogleMapsLatLng(
      mapData.center.lat,
      mapData.center.lon,
    );
    if (centerLatLng) {
      bounds?.extend(centerLatLng);
    }

    const sortedStores = stores
      .map((store) => {
        const mustZoom = !!store.mapMarkers.find(
          (mapMarker) =>
            mapMarker.displayType ===
            MapMarkerDisplayTypeEnum.ProminentImageContainer,
        );

        const mapMarkersWithDistance = [store.address, ...store.mapMarkers].map(
          (location) => {
            const distance = mapData.center
              ? getMilesBetween(location, mapData.center)
              : store.distanceMi || Infinity;

            return { ...location, distance };
          },
        );

        const selectedMapMarkers = mapMarkersWithDistance
          .sort((a, b) => a.distance - b.distance)
          .slice(0, 1);

        return { ...store, mustZoom, selectedMapMarkers };
      })
      .sort((a, b) => {
        // if both have same zoom priority, sort by distance
        if (a.mustZoom === b.mustZoom) {
          return (
            a.selectedMapMarkers[0].distance - b.selectedMapMarkers[0].distance
          );
        } else if (a.mustZoom) {
          return -1;
        } else {
          return 1;
        }
      });

    const selectedStores = [];
    for (const store of sortedStores) {
      if (store.mustZoom || selectedStores.length < storesShown) {
        selectedStores.push(store);
      } else {
        break;
      }
    }

    const points = await Promise.all(
      selectedStores.flatMap((store) =>
        store.selectedMapMarkers.map((marker) =>
          initializeGoogleMapsLatLng(marker.lat, marker.lon),
        ),
      ),
    );
    points.forEach((point) => {
      if (point) {
        bounds?.extend(point);
      }
    });
  }

  const neLng = bounds?.getNorthEast().lng();
  const neLat = bounds?.getNorthEast().lat();
  const swLat = bounds?.getSouthWest().lat();
  const swLng = bounds?.getSouthWest().lng();

  if (swLng === neLng || neLat === swLat) {
    if (bounds && forceReturn) {
      bounds.isSinglePoint = true;
    } else {
      return;
    }
  }

  return bounds;
};
